'use client'

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import { useTranslations } from 'next-intl'
import { type FormEvent, useEffect, useState } from 'react'

/** The longest title the assistant API accepts. */
const MAX_TITLE_LENGTH = 160

type RenameConversationDialogProps = {
  /** The title to start from; the dialog is closed while this is undefined. */
  title?: string
  isSaving: boolean
  onSave: (title: string) => void
  onClose: () => void
}

/** Asks for a conversation's new title. */
export default function RenameConversationDialog({ title, isSaving, onSave, onClose }: RenameConversationDialogProps) {
  const t = useTranslations('features.AssistantV2')
  const [value, setValue] = useState('')

  useEffect(() => {
    if (title !== undefined) setValue(title)
  }, [title])

  const trimmed = value.trim()
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (trimmed) onSave(trimmed)
  }

  return (
    <Dialog open={title !== undefined} onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>{t('renameTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label={t('renameLabel')}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_TITLE_LENGTH } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="contained" disabled={!trimmed || isSaving}>
            {t('renameSave')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
